<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div style="display:flex;align-items:center;gap:10px">
        <router-link to="/super-admin/companies" class="gl-back-btn" title="Back to companies">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </router-link>
        <div>
          <h1 class="gl-page-title">{{ company?.name || 'Company Details' }}</h1>
          <div class="gl-breadcrumb">
            <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
            <span class="gl-breadcrumb-sep">/</span>
            <router-link to="/super-admin/companies" class="gl-breadcrumb-link">Companies</router-link>
            <span class="gl-breadcrumb-sep">/</span>
            <span>{{ company?.name || '…' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Skeleton ── -->
    <div v-if="loading">
      <!-- Hero skeleton -->
      <div class="gl-card gl-hero-card" style="margin-bottom:16px">
        <div class="gl-hero-body">
          <div style="display:flex;align-items:center;gap:14px;flex:1;min-width:0">
            <div class="gl-skel" style="width:56px;height:56px;border-radius:12px;flex-shrink:0"></div>
            <div style="flex:1;min-width:0">
              <div class="gl-skel" style="height:18px;width:55%;border-radius:8px;margin-bottom:8px"></div>
              <div class="gl-skel" style="height:12px;width:75%;border-radius:6px"></div>
            </div>
          </div>
          <div class="gl-skel" style="width:80px;height:30px;border-radius:8px;flex-shrink:0"></div>
        </div>
      </div>
      <!-- Stats skeleton -->
      <div class="gl-stats-grid" style="margin-bottom:16px">
        <div v-for="i in 4" :key="i" class="gl-stat-card">
          <div class="gl-skel" style="width:40px;height:40px;border-radius:10px;flex-shrink:0"></div>
          <div style="flex:1">
            <div class="gl-skel" style="height:10px;width:60%;border-radius:6px;margin-bottom:7px"></div>
            <div class="gl-skel" style="height:20px;width:45%;border-radius:6px"></div>
          </div>
        </div>
      </div>
      <!-- Tabs skeleton -->
      <div class="gl-card">
        <div class="gl-tabs-bar" style="border-bottom:1px solid #f0f3f2;padding:0 20px">
          <div v-for="i in 4" :key="i" class="gl-skel" style="height:12px;width:60px;border-radius:6px;margin:14px 8px 14px 0"></div>
        </div>
        <div class="gl-card-body-pad">
          <div class="gl-skel" style="height:13px;width:140px;border-radius:6px;margin-bottom:18px"></div>
          <div v-for="i in 5" :key="i" style="display:flex;gap:20px;margin-bottom:14px">
            <div class="gl-skel" style="height:12px;width:110px;border-radius:6px"></div>
            <div class="gl-skel" style="height:12px;flex:1;border-radius:6px"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Content ── -->
    <div v-else-if="company">
      <!-- Company hero card -->
      <div class="gl-card gl-hero-card">
        <div class="gl-hero-body">
          <div class="gl-hero-left">
            <div class="gl-co-avatar-lg" v-if="company.logo">
              <img :src="company.logo" :alt="company.name" />
            </div>
            <div class="gl-co-avatar-lg gl-co-avatar-initial" v-else>
              {{ company.name?.charAt(0) }}
            </div>
            <div class="gl-hero-info">
              <h2 class="gl-hero-name">{{ company.name }}</h2>
              <div class="gl-hero-meta">
                <span class="gl-hero-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  {{ company.slug }}
                </span>
                <span class="gl-hero-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {{ company.email }}
                </span>
                <span class="gl-hero-meta-item" v-if="company.phone">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.51 5.51l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {{ company.phone }}
                </span>
              </div>
            </div>
          </div>
          <div class="gl-hero-right">
            <span :class="company.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-inactive'" style="font-size:12px;padding:4px 12px">
              {{ company.is_active ? 'Active' : 'Inactive' }}
            </span>
            <button
              class="gl-toggle-btn"
              :class="company.is_active ? 'gl-toggle-danger' : 'gl-toggle-success'"
              @click="toggleCompany"
            >
              {{ company.is_active ? 'Deactivate' : 'Activate' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Stats grid -->
      <div class="gl-stats-grid">
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Revenue</div>
            <div class="gl-stat-value">RM {{ formatCurrency(company.stats?.total_revenue) }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-teal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Orders</div>
            <div class="gl-stat-value">{{ company.stats?.total_orders || 0 }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-amber">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Outlets</div>
            <div class="gl-stat-value">{{ company.stats?.outlets_count || 0 }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Customers</div>
            <div class="gl-stat-value">{{ company.stats?.total_customers || 0 }}</div>
          </div>
        </div>
      </div>

      <!-- Tabs card -->
      <div class="gl-card">
        <div class="gl-tabs-bar">
          <button v-for="tab in tabs" :key="tab.key" class="gl-tab" :class="{ 'gl-tab-active': activeTab === tab.key }" @click="switchTab(tab.key)">
            {{ tab.label }}
          </button>
        </div>

        <div class="gl-tab-content">

          <!-- Overview -->
          <div v-if="activeTab === 'overview'" class="gl-overview-grid">
            <div>
              <div class="gl-section-title">Company Information</div>
              <div class="gl-info-table">
                <div class="gl-info-row">
                  <span class="gl-info-key">Business Type</span>
                  <span class="gl-info-val">{{ company.business_type || '—' }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Address</span>
                  <span class="gl-info-val">{{ company.address || '—' }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Subdomain</span>
                  <span class="gl-info-val gl-val-mono">{{ company.subdomain || '—' }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Custom Domain</span>
                  <span class="gl-info-val gl-val-mono">{{ company.custom_domain || '—' }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Created</span>
                  <span class="gl-info-val">{{ formatDate(company.created_at) }}</span>
                </div>
              </div>
            </div>

            <div>
              <div class="gl-section-title">Subscription</div>
              <div v-if="company.subscription" class="gl-info-table">
                <div class="gl-info-row">
                  <span class="gl-info-key">Plan</span>
                  <span class="gl-info-val">{{ company.subscription.plan?.name || '—' }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Status</span>
                  <span class="gl-info-val">
                    <span :class="getSubBadge(company.subscription.status)">{{ company.subscription.status }}</span>
                  </span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Billing Cycle</span>
                  <span class="gl-info-val">{{ company.subscription.billing_cycle }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Period Start</span>
                  <span class="gl-info-val">{{ formatDate(company.subscription.current_period_start) }}</span>
                </div>
                <div class="gl-info-row">
                  <span class="gl-info-key">Period End</span>
                  <span class="gl-info-val">{{ formatDate(company.subscription.current_period_end) }}</span>
                </div>
              </div>
              <div v-else class="gl-empty-msg" style="padding:16px 0;text-align:left">No active subscription</div>
            </div>
          </div>

          <!-- Outlets -->
          <div v-else-if="activeTab === 'outlets'">
            <div v-if="!company.outlets?.length" class="gl-empty-msg">No outlets found</div>
            <div v-else class="gl-tbl-scroll">
              <table class="gl-tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="outlet in company.outlets" :key="outlet.id">
                    <td class="gl-td-bold">{{ outlet.name }}</td>
                    <td class="gl-td-muted">{{ outlet.address || '—' }}</td>
                    <td>
                      <span :class="outlet.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-inactive'">
                        {{ outlet.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Admins -->
          <div v-else-if="activeTab === 'admins'">
            <div v-if="!company.admins?.length" class="gl-empty-msg">No admins found</div>
            <div v-else class="gl-tbl-scroll">
              <table class="gl-tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="admin in company.admins" :key="admin.id">
                    <td class="gl-td-bold">{{ admin.name }}</td>
                    <td class="gl-td-muted">{{ admin.email }}</td>
                    <td>
                      <span v-if="admin.is_company_owner" class="gl-badge gl-badge-info">Owner</span>
                      <span v-else class="gl-badge gl-badge-muted">Staff</span>
                    </td>
                    <td>
                      <span :class="admin.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-inactive'">
                        {{ admin.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Orders -->
          <div v-else-if="activeTab === 'orders'">
            <div v-if="loadingOrders" class="gl-orders-skel">
              <div v-for="i in 5" :key="i" class="gl-skel-row">
                <div class="gl-skel" style="height:12px;flex:1;border-radius:6px"></div>
                <div class="gl-skel" style="height:12px;flex:1.2;border-radius:6px"></div>
                <div class="gl-skel" style="height:12px;flex:1;border-radius:6px"></div>
                <div class="gl-skel" style="height:12px;width:64px;border-radius:6px"></div>
                <div class="gl-skel" style="height:20px;width:70px;border-radius:20px"></div>
                <div class="gl-skel" style="height:20px;width:60px;border-radius:20px"></div>
                <div class="gl-skel" style="height:12px;width:90px;border-radius:6px"></div>
              </div>
            </div>
            <div v-else-if="!orders.length" class="gl-empty-msg">No orders found</div>
            <div v-else class="gl-tbl-scroll">
              <table class="gl-tbl">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Outlet</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in orders" :key="order.id">
                    <td class="gl-td-mono">{{ order.order_number }}</td>
                    <td class="gl-td-bold">{{ order.customer?.name || '—' }}</td>
                    <td class="gl-td-muted">{{ order.outlet?.name || '—' }}</td>
                    <td class="gl-td-bold">RM {{ formatCurrency(order.total) }}</td>
                    <td><span :class="getStatusBadge(order.status)">{{ order.status }}</span></td>
                    <td><span :class="getPaymentBadge(order.payment_status)">{{ order.payment_status }}</span></td>
                    <td class="gl-td-muted">{{ formatDate(order.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Error / not found -->
    <div v-else class="gl-empty-msg" style="padding: 60px 0">Company not found.</div>
  </div>
</template>

<script>
import { superAdminCompanyApi } from '@/services/superAdminApi';

export default {
  name: 'CompanyDetail',
  data() {
    return {
      loading: false,
      loadingOrders: false,
      company: null,
      orders: [],
      activeTab: 'overview',
      tabs: [
        { key: 'overview', label: 'Overview' },
        { key: 'outlets',  label: 'Outlets'  },
        { key: 'admins',   label: 'Admins'   },
        { key: 'orders',   label: 'Orders'   }
      ]
    };
  },
  created() {
    this.fetchCompany();
  },
  methods: {
    switchTab(key) {
      this.activeTab = key;
      if (key === 'orders') this.fetchOrders();
    },
    async fetchCompany() {
      this.loading = true;
      try {
        const response = await superAdminCompanyApi.get(this.$route.params.id);
        if (response.data.success) this.company = response.data.data;
      } catch (error) {
        console.error('Failed to fetch company:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchOrders() {
      if (this.orders.length > 0) return;
      this.loadingOrders = true;
      try {
        const response = await superAdminCompanyApi.getOrders(this.$route.params.id);
        if (response.data.success) this.orders = response.data.data;
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        this.loadingOrders = false;
      }
    },
    async toggleCompany() {
      try {
        const response = await superAdminCompanyApi.toggle(this.$route.params.id);
        if (response.data.success) this.company.is_active = response.data.data.is_active;
      } catch (error) {
        console.error('Failed to toggle company:', error);
      }
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getSubBadge(status) {
      return { active: 'gl-badge gl-badge-active', trialing: 'gl-badge gl-badge-pending', past_due: 'gl-badge gl-badge-inactive', cancelled: 'gl-badge gl-badge-muted' }[status] || 'gl-badge gl-badge-info';
    },
    getStatusBadge(status) {
      return { pending: 'gl-badge gl-badge-pending', confirmed: 'gl-badge gl-badge-info', preparing: 'gl-badge gl-badge-preparing', ready: 'gl-badge gl-badge-active', completed: 'gl-badge gl-badge-active', cancelled: 'gl-badge gl-badge-inactive' }[status] || 'gl-badge gl-badge-muted';
    },
    getPaymentBadge(status) {
      return { pending: 'gl-badge gl-badge-pending', paid: 'gl-badge gl-badge-active', failed: 'gl-badge gl-badge-inactive', refunded: 'gl-badge gl-badge-info' }[status] || 'gl-badge gl-badge-muted';
    }
  }
};
</script>

<style scoped>
/* ── Page shell ── */
.gl-page { padding: 24px 28px 48px; }

.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.4px; margin: 0 0 4px; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8a9e9a; }
.gl-breadcrumb-link { color: #900C27; text-decoration: none; }
.gl-breadcrumb-link:hover { text-decoration: underline; }
.gl-breadcrumb-sep { opacity: .5; }
.gl-back-btn { width: 34px; height: 34px; border-radius: 9px; border: 1px solid #e0e7e5; background: #fff; display: flex; align-items: center; justify-content: center; color: #900C27; cursor: pointer; text-decoration: none; flex-shrink: 0; }
.gl-back-btn:hover { background: #f0f3f2; }

/* ── Hero card ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }
.gl-hero-card { margin-bottom: 16px; }
.gl-hero-body { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; flex-wrap: wrap; }
.gl-hero-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 0; }
.gl-hero-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; flex-wrap: wrap; }
.gl-co-avatar-lg { width: 56px; height: 56px; border-radius: 12px; overflow: hidden; flex-shrink: 0; }
.gl-co-avatar-lg img { width: 100%; height: 100%; object-fit: cover; }
.gl-co-avatar-initial { background: rgba(144,12,39,.1); color: #900C27; font-size: 22px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.gl-hero-info { min-width: 0; }
.gl-hero-name { font-size: 18px; font-weight: 800; color: #1a2e2b; margin: 0 0 6px; }
.gl-hero-meta { display: flex; flex-wrap: wrap; gap: 10px 16px; }
.gl-hero-meta-item { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #6b7f7b; }
.gl-toggle-btn { padding: 7px 16px; border-radius: 8px; border: 1px solid; font-size: 13px; font-weight: 600; cursor: pointer; background: transparent; white-space: nowrap; }
.gl-toggle-danger { border-color: #dc2626; color: #dc2626; }
.gl-toggle-danger:hover { background: rgba(220,38,38,.06); }
.gl-toggle-success { border-color: #16a34a; color: #16a34a; }
.gl-toggle-success:hover { background: rgba(22,163,74,.06); }

/* ── Badges ── */
.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; text-transform: capitalize; }
.gl-badge-active    { background: #e0f0ec; color: #1a5248; }
.gl-badge-inactive  { background: #fce7e7; color: #dc2626; }
.gl-badge-pending   { background: #fef3c7; color: #92400e; }
.gl-badge-preparing { background: #ede9fe; color: #6d28d9; }
.gl-badge-info      { background: #dbeafe; color: #1d4ed8; }
.gl-badge-muted     { background: #f0f3f2; color: #6b7f7b; }

/* ── Stats grid ── */
.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 16px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-icon-green { background: rgba(22,163,74,.12);  color: #16a34a; }
.gl-icon-amber { background: rgba(245,158,11,.12); color: #d97706; }
.gl-stat-label { font-size: 11px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.gl-stat-value { font-size: 20px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.5px; }

/* ── Tabs ── */
.gl-tabs-bar { display: flex; gap: 2px; padding: 14px 20px 0; border-bottom: 1px solid #f0f3f2; overflow-x: auto; }
.gl-tab { padding: 9px 16px 11px; font-size: 13px; font-weight: 600; color: #8a9e9a; background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; transition: all .15s; white-space: nowrap; }
.gl-tab:hover { color: #900C27; }
.gl-tab-active { color: #900C27; border-bottom-color: #900C27; }
.gl-tab-content { padding: 22px 24px; }

/* ── Overview ── */
.gl-overview-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
.gl-section-title { font-size: 13px; font-weight: 700; color: #1a2e2b; margin-bottom: 14px; }
.gl-info-table { display: flex; flex-direction: column; gap: 0; }
.gl-info-row { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f5f7f6; }
.gl-info-row:last-child { border-bottom: none; }
.gl-info-key { font-size: 12.5px; color: #8a9e9a; min-width: 130px; flex-shrink: 0; }
.gl-info-val { font-size: 13px; color: #2a3d3a; font-weight: 500; word-break: break-word; }
.gl-val-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: #900C27; }

/* ── Table ── */
.gl-tbl-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gl-tbl { width: 100%; border-collapse: collapse; min-width: 560px; }
.gl-tbl thead th { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; padding: 11px 16px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-tbl tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-tbl tbody tr:last-child { border-bottom: none; }
.gl-tbl tbody tr:hover { background: #f9fbfa; }
.gl-tbl tbody td { padding: 12px 16px; font-size: 13px; color: #2a3d3a; }
.gl-td-muted { color: #6b7f7b !important; }
.gl-td-bold { font-weight: 700; color: #1a2e2b !important; }
.gl-td-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: #900C27 !important; font-weight: 600; }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-row { display: flex; align-items: center; gap: 16px; padding: 14px 0; border-bottom: 1px solid #f5f7f6; }
.gl-orders-skel { padding: 0 2px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Empty ── */
.gl-empty-msg { font-size: 13px; color: #a0b5b0; padding: 32px 0; text-align: center; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 14px; }
  .gl-breadcrumb { display: none; }

  .gl-hero-body { padding: 16px; flex-direction: column; align-items: flex-start; }
  .gl-hero-right { width: 100%; justify-content: flex-start; }
  .gl-hero-name { font-size: 16px; }
  .gl-co-avatar-lg { width: 46px; height: 46px; }

  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-icon { width: 36px; height: 36px; border-radius: 9px; }
  .gl-stat-value { font-size: 17px; }

  .gl-tabs-bar { padding: 10px 16px 0; gap: 0; }
  .gl-tab { padding: 8px 12px 10px; font-size: 12.5px; }
  .gl-tab-content { padding: 16px; }

  .gl-overview-grid { grid-template-columns: 1fr; gap: 20px; }
  .gl-info-key { min-width: 110px; }

  .gl-skel-row { padding: 12px 0; gap: 10px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-back-btn { background: #1e2d2b; border-color: #2a3d3a; color: #3d9e8c; }
body.dark-only .gl-back-btn:hover { background: #243432; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-hero-name { color: #e0eded; }
body.dark-only .gl-hero-meta-item { color: #5a7470; }
body.dark-only .gl-co-avatar-initial { background: rgba(144,12,39,.2); }
body.dark-only .gl-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-stat-label { color: #5a7470; }
body.dark-only .gl-stat-value { color: #e0eded; }
body.dark-only .gl-tabs-bar { border-bottom-color: #2a3d3a; }
body.dark-only .gl-tab { color: #5a7470; }
body.dark-only .gl-tab:hover { color: #3d9e8c; }
body.dark-only .gl-tab-active { color: #3d9e8c; border-bottom-color: #3d9e8c; }
body.dark-only .gl-section-title { color: #e0eded; }
body.dark-only .gl-info-row { border-bottom-color: #2a3d3a; }
body.dark-only .gl-info-key { color: #5a7470; }
body.dark-only .gl-info-val { color: #c0d4d0; }
body.dark-only .gl-val-mono { color: #3d9e8c; }
body.dark-only .gl-tbl thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-tbl tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-tbl tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-tbl tbody td { color: #c0d4d0; }
body.dark-only .gl-td-muted { color: #5a7470 !important; }
body.dark-only .gl-td-bold { color: #e0eded !important; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-skel-row { border-bottom-color: #222f2d; }
body.dark-only .gl-empty-msg { color: #5a7470; }
</style>
